import { useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import { SystemConnectingBanner } from '../../components/shared/SystemConnectingBanner';
import { getLeaveSummary, listLeaves, listBusinessTrips, listOvertime, type LeaveSummaryDto, type LeaveDto, type TripDto, type OvertimeDto } from '../../api/hr';
import { leaveAvailable } from '../../lib/leave-balance';
import { getSharedLang, type Lang } from '../../lib/base-i18n';
// 휴가 길이는 분이 진실 (2026-08-24) — 서버가 만든 문구를 화면이 그대로 쓴다.
import { leaveAmountText } from '../../lib/leave-amount';
// 2026-09-16 — 휴가 종류의 **한국어 이름**은 공용 목록 하나에서 온다(사본 합치기).
//   여기만 「오전반차 · 특별휴가」로 달랐다 — 사용자 결정으로 「반차 · 반반차 · 경조」 어휘에 맞춘다.
import { LEAVE_TYPE_LABEL } from '../../lib/leave-type';
// S6 — 반차 · 반반차에 저장된 구간이 있으면 「14:00–16:00」을 붙인다(옛 기록은 없음).
import { leaveTimeRangeText } from '../../lib/partial-leave-time';

// Cycle 53 — local i18n dict (cycle 49/52 패턴 일관). 27 strings × 3 lang.
// 2026-09-16 — 휴가 코드 union 은 지웠다(사본 다섯째). 코드는 `lib/leave-type.ts` 가 안다.
//   ★ 이 표는 **모르는 코드도 그대로** 보여야 한다(회사가 만든 종류 · 서버가 새로 더한 코드) —
//     그래서 `Record<string, string>` 로 받고 없으면 코드를 그대로 찍는다.
//   ★ S6 의 합친 코드(`half` · `quarter`)도 그 목록에 함께 있으므로 여기서 union 을 늘릴 일이 없다.
type LeaveStatusKey = 'APPROVED' | 'PENDING' | 'REJECTED' | 'CANCELED';
const HR_MESSAGES: Record<Lang, {
  page_title: string;
  err_fetch: string;
  loading: string;
  system_name: string;
  system_detail: string;
  section_summary: string;
  /** ★ «내 휴가» 화면(HrMenu/MyLeavePage)의 제목 — **메뉴 이름과 같아야 한다**.
   *  `section_summary`(휴가 현황)는 옛 HRPage 의 소제목이라 따로 둔다(2026-08-27). */
  page_my_leave: string;
  card_total: string;
  card_used: string;
  card_pending: string;
  card_remaining: string;
  card_sub_remaining: string;
  unit_days: string;
  unit_hours: string;
  year_suffix: string;
  section_monthly: string;
  section_leave_requests: string;
  btn_request_leave: string;
  empty_leave_requests: string;
  section_trips: string;
  btn_request_trip: string;
  empty_trips: string;
  empty_general: string;
  section_overtime: string;
  btn_request_overtime: string;
  col_type: string;
  col_period: string;
  col_days: string;
  col_status: string;
  col_destination: string;
  col_date: string;
  col_hours: string;
  col_reason: string;
  leave_type: Record<string, string>;
  leave_status: Record<LeaveStatusKey, string>;
  month_suffix: string;
  monthly_chart_aria: (month: number, days: number) => string;
}> = {
  ko: {
    page_title: '인사 (HR)',
    err_fetch: '인사 데이터 로딩 실패',
    loading: '로딩 중…',
    system_name: '인사 시스템',
    system_detail: '아직 신청 내역이 없거나 외부 HRIS 연동 대기 중입니다.',
    section_summary: '휴가 현황',
    page_my_leave: '내 휴가 · 출장 · 잔업',
    card_total: '연차 총', card_used: '사용', card_pending: '신청 중',
    card_remaining: '잔여', card_sub_remaining: '보상휴가 잔여',
    unit_days: '일', unit_hours: 'h', year_suffix: '년',
    section_monthly: '월별 연차 사용',
    section_leave_requests: '휴가 신청 내역',
    btn_request_leave: '+ 휴가 신청',
    empty_leave_requests: '신청 내역이 없습니다.',
    section_trips: '출장 / 외근',
    btn_request_trip: '+ 출장 신청',
    empty_trips: '내역이 없습니다.',
    empty_general: '내역이 없습니다.',
    section_overtime: '잔업 / 야근',
    btn_request_overtime: '+ 잔업 신청',
    col_type: '구분', col_period: '기간', col_days: '일수', col_status: '상태',
    col_destination: '목적지', col_date: '날짜', col_hours: '시간', col_reason: '사유',
    /*
     * 2026-09-16 — **공용 목록 하나에서 온다**(사본 다섯째를 지웠다).
     *
     * ★ 사용자 결정: 어휘는 「반차 · 반반차 · 경조」로 통일한다 — 종전 여기만
     *   「오전반차 · 오후반반차 · 특별휴가」라 같은 종류가 화면마다 다른 이름으로 보였다.
     * ★★★ **오전/오후는 괄호로 남긴다.** 이 표는 **지난 기록**을 보여 주는 자리라
     *     「반차」 하나로 합치면 사람이 오전/오후를 못 가린다(관제 2026-09-16 경계).
     * ★ S6 의 합친 코드(`half` · `quarter`)는 **공용 카탈로그에 들어 있어** 여기서 더할 것이 없다 —
     *   그래서 새 코드는 「반차 · 반반차」, 옛 기록은 「반차(오전)」으로 **저절로** 갈린다.
     */
    leave_type: LEAVE_TYPE_LABEL,
    // ★ CANCELED = 기안자가 «회수» 한 것. 「반려」로 적으면 반려한 사람이 없는데 있는 것처럼 읽힌다.
    leave_status: { APPROVED: '승인', PENDING: '대기', REJECTED: '반려', CANCELED: '회수' },
    month_suffix: '월',
    monthly_chart_aria: (m, d) => `${m}월 ${d}일`,
  },
  en: {
    page_title: 'HR',
    err_fetch: 'Failed to load HR data',
    loading: 'Loading…',
    system_name: 'HR system',
    system_detail: 'No requests yet or awaiting external HRIS integration.',
    section_summary: 'Leave overview',
    page_my_leave: 'My leave · trips · overtime',
    card_total: 'Annual total', card_used: 'Used', card_pending: 'Requesting',
    card_remaining: 'Remaining', card_sub_remaining: 'Substitute remaining',
    unit_days: 'd', unit_hours: 'h', year_suffix: '',
    section_monthly: 'Monthly annual leave usage',
    section_leave_requests: 'Leave requests',
    btn_request_leave: '+ Request leave',
    empty_leave_requests: 'No requests.',
    section_trips: 'Business trip / outwork',
    btn_request_trip: '+ Request trip',
    empty_trips: 'No records.',
    empty_general: 'No records.',
    section_overtime: 'Overtime / late work',
    btn_request_overtime: '+ Request overtime',
    col_type: 'Type', col_period: 'Period', col_days: 'Days', col_status: 'Status',
    col_destination: 'Destination', col_date: 'Date', col_hours: 'Hours', col_reason: 'Reason',
    leave_type: {
      // 2026-09-16 — 낱말은 그대로 두고 오전/오후만 **코드 기준**으로 정리한다(ko 와 같은 꼴).
      //   S6 의 합친 코드 둘은 여기(지역 표)에만 더한다 — ko 와 달리 공용 목록이 없다.
      annual: 'Annual', half: 'Half day', quarter: 'Quarter day',
      half_am: 'Half day (AM)', half_pm: 'Half day (PM)',
      quarter_am: 'Quarter day (AM)', quarter_pm: 'Quarter day (PM)',
      sick: 'Sick', special: 'Special', substitute: 'Substitute',
    },
    leave_status: { APPROVED: 'Approved', PENDING: 'Pending', REJECTED: 'Rejected', CANCELED: 'Recalled' },
    month_suffix: '',
    monthly_chart_aria: (m, d) => `Month ${m}: ${d} days`,
  },
  ja: {
    page_title: '人事 (HR)',
    err_fetch: '人事データ取得失敗',
    loading: '読み込み中...',
    system_name: '人事システム',
    system_detail: 'まだ申請がないか、外部HRIS連携待ちです。',
    section_summary: '休暇状況',
    page_my_leave: '私の休暇・出張・残業',
    card_total: '年休合計', card_used: '使用', card_pending: '申請中',
    card_remaining: '残り', card_sub_remaining: '代替休暇残り',
    unit_days: '日', unit_hours: 'h', year_suffix: '年',
    section_monthly: '月別年休使用',
    section_leave_requests: '休暇申請履歴',
    btn_request_leave: '+ 休暇申請',
    empty_leave_requests: '申請がありません。',
    section_trips: '出張 / 外勤',
    btn_request_trip: '+ 出張申請',
    empty_trips: '履歴がありません。',
    empty_general: '履歴がありません。',
    section_overtime: '残業 / 夜勤',
    btn_request_overtime: '+ 残業申請',
    col_type: '区分', col_period: '期間', col_days: '日数', col_status: 'ステータス',
    col_destination: '目的地', col_date: '日付', col_hours: '時間', col_reason: '理由',
    leave_type: {
      // 2026-09-16 — 낱말은 그대로 두고 오전/오후만 **코드 기준**으로 정리한다(ko 와 같은 꼴).
      //   S6 의 합친 코드 둘은 여기(지역 표)에만 더한다 — ko 와 달리 공용 목록이 없다.
      annual: '年休', half: '半休', quarter: '1/4休',
      half_am: '半休(午前)', half_pm: '半休(午後)',
      quarter_am: '1/4休(午前)', quarter_pm: '1/4休(午後)',
      sick: '病気', special: '特別休暇', substitute: '代替休暇',
    },
    leave_status: { APPROVED: '承認', PENDING: '待機', REJECTED: '却下', CANCELED: '回収' },
    month_suffix: '月',
    monthly_chart_aria: (m, d) => `${m}月 ${d}日`,
  },
};

export function tHR(): typeof HR_MESSAGES['ko'] {
  return HR_MESSAGES[getSharedLang()] ?? HR_MESSAGES.en;
}

/**
 * HRPage — 기능정의서 §13 인사 (slfg --from-doc init-docs/, scope A 후 read-wire).
 *
 * 본 cycle:
 *   - GET /hr/leaves/summary + /hr/leaves + /hr/business-trips + /hr/overtime 실 API 호출
 *   - 신청 form (POST) UI 는 별도 cycle — 신청 버튼은 disabled 유지 + 안내 변경
 *   - 빈 응답 시 SystemConnectingBanner 보존 (외부 인사 시스템 연동 대기 안내)
 */

export function HRPage() {
  const [summary, setSummary] = useState<LeaveSummaryDto | null>(null);
  const [leaves, setLeaves] = useState<LeaveDto[]>([]);
  const [trips, setTrips] = useState<TripDto[]>([]);
  const [overtime, setOvertime] = useState<OvertimeDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [s, l, t, o] = await Promise.all([
          getLeaveSummary(),
          listLeaves(),
          listBusinessTrips(),
          listOvertime(),
        ]);
        if (cancelled) return;
        setSummary(s);
        setLeaves(l);
        setTrips(t);
        setOvertime(o);
      } catch (err) {
        if (cancelled) return;
        const ax = err as AxiosError<{ message?: string }>;
        setError(ax.response?.data?.message ?? tHR().err_fetch);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const t = tHR();

  if (loading) {
    return <main style={{ padding: 24 }}><h1>{t.page_title}</h1><p>{t.loading}</p></main>;
  }

  const isEmpty = !summary || (leaves.length === 0 && trips.length === 0 && overtime.length === 0);

  return (
    <main style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <h1>{t.page_title}</h1>

      {error && (
        <div role="alert" style={{ background: '#fee2e2', color: '#991b1b', padding: 12, borderRadius: 6, marginBottom: 12 }}>
          {error}
        </div>
      )}

      {isEmpty && !error && (
        <SystemConnectingBanner
          systemName={t.system_name}
          detail={t.system_detail}
        />
      )}

      {/* 연차 카드 */}
      {summary && (
        <section aria-labelledby="leave-summary">
          <h2 id="leave-summary">{t.section_summary} ({summary.year}{t.year_suffix})</h2>
          <div style={cardGridStyle}>
            <div style={cardStyle}><div style={cardLabel}>{t.card_total}</div><div style={cardValue}>{summary.annual.total ?? '—'}{t.unit_days}</div></div>
            <div style={cardStyle}><div style={cardLabel}>{t.card_used}</div><div style={cardValue}>{summary.annual.used}{t.unit_days}</div></div>
            <div style={cardStyle}><div style={cardLabel}>{t.card_pending}</div><div style={cardValue}>{summary.annual.pending}{t.unit_days}</div></div>
            <div style={cardStyle}>
              <div style={cardLabel}>{t.card_remaining}</div>
              <div style={{ ...cardValue, color: '#059669' }}>{leaveAvailable(summary.annual) ?? '—'}{t.unit_days}</div>
            </div>
            <div style={cardStyle}><div style={cardLabel}>{t.card_sub_remaining}</div><div style={cardValue}>{summary.substitute.remaining}{t.unit_days}</div></div>
          </div>
        </section>
      )}

      {/* 월별 차트 */}
      {summary && (
        <section aria-labelledby="monthly-chart" style={{ marginTop: 24 }}>
          <h2 id="monthly-chart">{t.section_monthly}</h2>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 100, padding: '12px 16px', background: '#f9fafb', borderRadius: 6 }}>
            {summary.monthlyUsage.map((days, idx) => (
              <div key={idx} style={{ flex: 1, textAlign: 'center' }}>
                <div
                  style={{
                    // 컨테이너 100px — 80px clamp 로 overflow 방지 (review correctness MEDIUM).
                    height: `${Math.min(80, days * 18)}px`,
                    background: days > 0 ? '#3b82f6' : '#e5e7eb',
                    marginBottom: 4,
                    borderRadius: 2,
                  }}
                  aria-label={t.monthly_chart_aria(idx + 1, days)}
                />
                <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{String(idx + 1).padStart(2, '0')}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* 휴가 신청 내역 */}
      <section aria-labelledby="leave-requests" style={{ marginTop: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 id="leave-requests">{t.section_leave_requests}</h2>
          <button disabled title="신청 form 은 다음 사이클에 활성" style={btnDisabledStyle}>{t.btn_request_leave}</button>
        </div>
        {leaves.length === 0 ? (
          <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>{t.empty_leave_requests}</p>
        ) : (
          <table style={tableStyle}>
            <thead><tr><th>{t.col_type}</th><th>{t.col_period}</th><th>{t.col_days}</th><th>{t.col_status}</th></tr></thead>
            <tbody>
              {leaves.map((r) => (
                <tr key={r.id}>
                  {/* 유형 이름은 공용 목록에서 · 캐스트 없이(모르는 코드도 그대로 보인다). */}
                  <td>{t.leave_type[r.type] ?? r.type}</td>
                  {/* S6 — 저장된 구간이 있으면 기간 뒤에 「14:00–16:00」을 붙인다(옛 기록은 없음). */}
                  <td>{r.startDate}{r.startDate !== r.endDate ? ` ~ ${r.endDate}` : ''}{leaveTimeRangeText(r) ? ` ${leaveTimeRangeText(r)}` : ''}</td>
                  {/* 길이는 분이 진실 — 서버 문구를 그대로 쓰고, 옛 응답이면 일수로 되돌아간다. */}
                  <td>{r.minutes != null || r.amountLabel ? leaveAmountText(r) : `${r.days}${t.unit_days}`}</td>
                  <td><StatusPill status={r.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* 출장/외근 */}
      <section aria-labelledby="trips" style={{ marginTop: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 id="trips">{t.section_trips}</h2>
          <button disabled title="신청 form 은 다음 사이클에 활성" style={btnDisabledStyle}>{t.btn_request_trip}</button>
        </div>
        {trips.length === 0 ? (
          <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>{t.empty_trips}</p>
        ) : (
          <table style={tableStyle}>
            <thead><tr><th>{t.col_destination}</th><th>{t.col_period}</th><th>{t.col_status}</th></tr></thead>
            <tbody>
              {trips.map((trip) => (
                <tr key={trip.id}>
                  <td>{trip.destination}</td>
                  <td>{trip.startDate}{trip.startDate !== trip.endDate ? ` ~ ${trip.endDate}` : ''}</td>
                  <td><StatusPill status={trip.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* 잔업/야근 */}
      <section aria-labelledby="overtime" style={{ marginTop: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 id="overtime">{t.section_overtime}</h2>
          <button disabled title="신청 form 은 다음 사이클에 활성" style={btnDisabledStyle}>{t.btn_request_overtime}</button>
        </div>
        {overtime.length === 0 ? (
          <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>{t.empty_general}</p>
        ) : (
          <table style={tableStyle}>
            <thead><tr><th>{t.col_date}</th><th>{t.col_hours}</th><th>{t.col_reason}</th><th>{t.col_status}</th></tr></thead>
            <tbody>
              {overtime.map((o) => (
                <tr key={o.id}>
                  <td>{o.date}</td>
                  <td>{o.hours}{t.unit_hours}</td>
                  <td>{o.reason}</td>
                  <td><StatusPill status={o.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </main>
  );
}

function StatusPill({ status }: { status: string }) {
  const colors: Record<string, { bg: string; fg: string }> = {
    APPROVED: { bg: '#d1fae5', fg: '#065f46' },
    PENDING: { bg: '#fef3c7', fg: '#92400e' },
    REJECTED: { bg: '#fee2e2', fg: '#991b1b' },
  };
  const c = colors[status] ?? { bg: '#e5e7eb', fg: '#374151' };
  const label = tHR().leave_status[status as LeaveStatusKey] ?? status;
  return (
    <span style={{ background: c.bg, color: c.fg, padding: '2px 8px', borderRadius: 12, fontSize: '0.75rem' }}>
      {label}
    </span>
  );
}

const cardGridStyle: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginTop: 8,
};
const cardStyle: React.CSSProperties = {
  padding: 14, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6,
};
const cardLabel: React.CSSProperties = { fontSize: '0.8rem', color: '#6b7280' };
const cardValue: React.CSSProperties = { fontSize: '1.5rem', fontWeight: 600, marginTop: 4 };
const tableStyle: React.CSSProperties = {
  width: '100%', borderCollapse: 'collapse', marginTop: 8, fontSize: '0.9rem',
};
const btnDisabledStyle: React.CSSProperties = {
  padding: '6px 12px', border: '1px solid #d1d5db', background: '#f3f4f6',
  color: '#9ca3af', borderRadius: 4, cursor: 'not-allowed', fontSize: '0.85rem',
};
